/* eslint-disable @typescript-eslint/no-unsafe-return */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { ChildrenOutletContexts, RouterOutlet } from '@angular/router';
import { ViewModelAwareInterface } from 'src/app/shared/models/view-model-aware.interface';
import { ReceiptStepsService } from '../../services/receipt-steps.service';
import { RECEIPT_LONG_OP_FULL_PATH } from 'src/app/shared/shared.module';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { StepViewModelAwareInterface } from 'src/app/shared/models/step-view-model-aware.interface';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { AuthService, FilledButtonComponent, LoaderComponent, MessageCodes, MessageResourceManager, ModalService, NTSTranslatePipe, TextButtonComponent } from '@nts/std';
import { stepTransitionAnimations } from 'src/app/shared/animations';
import { ExpenseState } from 'src/app/expense-annotation/generated/domain-models/enums/generated-expense-state';
import { AsyncPipe, NgIf } from '@angular/common';
import { TelemetryService } from '@nts/std/telemetry';
import { MessageButton, MessageResult } from '@nts/std/types';

@UntilDestroy()
@Component({
  selector: 'nts-receipt-step-container',
  templateUrl: './receipt-step-container.component.html',
  styleUrls: ['./receipt-step-container.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TextButtonComponent,
    LoaderComponent,
    RouterOutlet,
    FilledButtonComponent,
    AsyncPipe,
    NgIf,
    NTSTranslatePipe
  ],
  // make slide in/out animation available to this component
  // animations: [slideInOutAnimation],

  // attach the slide in/out animation to the host (root) element of this component
  // host: { '[@slideInOutAnimation]': '' }
  animations: [stepTransitionAnimations]
})
export class ReceiptStepContainerComponent {
  currentViewModel: StepViewModelAwareInterface;
  stepLoading: boolean;
  stepLoadingText: string;
  nextStepEnabled: boolean;
  baseRoutePath = RECEIPT_LONG_OP_FULL_PATH;
  defaultColor = '#3e709b';
  currentColor = this.defaultColor;
  forwordButtonLabel = '';
  forwordWithNoReceiptButtonLabel = MessageResourceManager.Current.getMessage('CMD_ForwardWithNoReceiptButtonLabel');
  isOnline$ = new BehaviorSubject<boolean>(false);

  isOnFirstStep(): boolean {
    return this.stepsService.isFirstStep();
  }

  isOnLastStep(): boolean {
    return this.stepsService.isLastStep();
  }

  constructor(
    public stepsService: ReceiptStepsService,
    private cd: ChangeDetectorRef,
    private contexts: ChildrenOutletContexts,
    private telemetryService: TelemetryService,
    private modalService: ModalService,
    private authService: AuthService,
  ) {

    this.stepsService.onlineService.isOnline$.pipe(untilDestroyed(this)).subscribe(() => {
      this.isOnline$.next(this.stepsService.onlineService.isOnline);
    });

    this.stepsService.stepLoading.pipe(untilDestroyed(this)).subscribe((stepLoading) => {
      this.stepLoading = stepLoading;
      this.cd.markForCheck();
    });
    this.stepsService.stepLoadingText.pipe(untilDestroyed(this)).subscribe((stepLoadingText) => {
      this.stepLoadingText = stepLoadingText;
      this.cd.markForCheck();
    });
    this.stepsService.nextStepEnabled.pipe(untilDestroyed(this)).subscribe((nextStepEnabled) => {
      this.nextStepEnabled = nextStepEnabled;
      this.cd.markForCheck();
    });
  }

  async onActivate(component: ViewModelAwareInterface) {
    await this.stepsService.init();
    await component.onViewModelLoaded();
    this.cd.detectChanges();
    this.stepsService.currentStepChanged.pipe(untilDestroyed(this), switchMap(() => this.getForwardButtonLabel())).subscribe((forwordButtonLabel: string) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.currentViewModel = this.stepsService.getCurrentState();
      this.forwordButtonLabel = forwordButtonLabel;
      this.cd.detectChanges();
    });
  }

  async forwardWithNoReceipt() {
    const rootViewModel = this.stepsService.orchestratorViewModel.rootViewModel;
    rootViewModel.forwardWithNoReceipt = true;
    await this.stepsService.nextStep();
  }

  async onNextStep(isDraft: boolean = null) {
    const rootViewModel = this.stepsService.orchestratorViewModel.rootViewModel;

    if (this.isOnFirstStep()) {
      rootViewModel.forwardWithNoReceipt = false;
    }

    if (isDraft != null) {
      rootViewModel.isDraft = isDraft;
    } else {
      const expenseAnnotationId = this?.stepsService?.expenseAnnotationId;
      const expenseState = this.stepsService.orchestratorViewModel.rootViewModel.params.expenseState;
      if ((expenseAnnotationId == undefined || expenseAnnotationId == null) && expenseState.value === ExpenseState.WebEditing && this.stepsService.isLastStep()) {
        rootViewModel.isDraft = false;
      } else {
        rootViewModel.isDraft = null;
      }
    }

    await this.stepsService.nextStep(false);
  }

  async onExit() {
    const warningMessage = MessageResourceManager.Current.getMessage(MessageCodes.Warning);
    const result = await this.modalService.showMessageAsync(
      warningMessage,
      MessageResourceManager.Current.getMessage('ReceiptLongOp_ExitMessage'),
      MessageButton.YesNo
    );

    if (result !== MessageResult.Yes) {
      return;
    }

    this.telemetryService.trackEvent({
      name: 'WEA_ReceiptLongOp_CloseButtonForExit',
    });
    await this.stepsService.exit();
  }

  async onPreviousStep() {
    await this.stepsService.previousStep();
  }

  getRouteAnimationData() {
    return this.contexts.getContext('primary')?.route?.snapshot?.data?.['animation'];
  }

  getForwardButtonLabel(): Observable<string> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.currentViewModel = this.stepsService.getCurrentState();
    if (this.currentViewModel?.customNextLabel) {
      return this.currentViewModel.customNextLabel;
    } else {

      return this.stepsService.currentStepChanged.pipe(switchMap(async () => {
        if (this.stepsService.isLastStep()) {

          let label = MessageResourceManager.Current.getMessage('CMD_ReceiptLastStep_StoreInDevice');
          const expenseAnnotationId = this?.stepsService?.expenseAnnotationId;
          const remoteExpense = this.stepsService.remoteExpense;
          const expenseState = this.stepsService.orchestratorViewModel.rootViewModel.params.expenseState;
          const expenseUserId = this.stepsService.orchestratorViewModel.rootViewModel?.params?.userRef?.id?.value;

          const currentUserId = await this.authService.getCurrentUserId();
          this.stepsService.isReadOnly$.next(currentUserId !== expenseUserId || (expenseState.value !== ExpenseState.WebEditing && expenseState.value !== ExpenseState.IntegrateDocumentation));
          this.isOnline$.next(this.stepsService.onlineService.isOnline);

          if (this.stepsService.isReadOnly$.value) {
            label =
              MessageResourceManager.Current.getMessage('CMD_ReceiptLastStep_Close');

          } else if (!this.isOnline$.value) {
            label = MessageResourceManager.Current.getMessage('CMD_ReceiptLastStep_StoreInDevice');
          } else if ((expenseAnnotationId == undefined || expenseAnnotationId == null) && expenseState.value === ExpenseState.WebEditing && this.stepsService?.isLastStep()) {
            // Salvo nel nuovo aggregato
            label = MessageResourceManager.Current.getMessage('CMD_ReceiptLastStep_CompleteDraft');
          } else if (expenseAnnotationId > 0 && remoteExpense === false && expenseState.value === ExpenseState.WebEditing) {
            // In questo caso sto usando una expense sul device e ho il riferimento della nota spesa
            // Quindi sono in creazione partendo da una spesa del device

            label =
              MessageResourceManager.Current.getMessage('CMD_ReceiptLastStep_Add');

            // In questo caso sto usando una expense remota e ho il riferimento della nota spesa
            // Quindi sono in modica di una spesa esistente di una determinata nota spesa
          } else if (expenseAnnotationId > 0 && remoteExpense === true && expenseState.value === ExpenseState.WebEditing) {
            label =
              MessageResourceManager.Current.getMessage('CMD_ReceiptLastStep_Update');

          } else if (expenseState.value === ExpenseState.IntegrateDocumentation) {
            label =
              MessageResourceManager.Current.getMessage('CMD_ReceiptLastStep_Integrate');

          }
          return label;
        } else {
          return MessageResourceManager.Current.getMessage('CMD_Next');
        }
      }));
    }
  }
}
